import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "../../../../lib/api";
import { toast } from "sonner";
import { Button } from "../../../../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../../components/ui/dialog";
import { Pencil } from "lucide-react";
import { Field, SectionCard, StatusPill } from "./shared";
import { useAccountOptions } from "./AccountSelect";
import SearchableSelect from "./SearchableSelect";
import { ledgerGroupLabels } from "./constants";

/**
 * Accounting Phase 6 - Billing Ledger Mapping settings page.
 * /client/society/accounting/settings/billing-mapping
 *
 * Backs two SEPARATE backend surfaces (see server.py Phase 6 section):
 *  - GET/PUT  /society/accounting/billing-settings   -> Member Receivable /
 *    Member Advance ledgers + the settlement Bank Account (non-cash) / Cash
 *    ledger used when posting a Bill Payment.
 *  - GET      /society/accounting/billing-mapping    -> one row per active
 *    Billing Head (plus the reserved Interest/Late Fee row), each optionally
 *    linked to an Income ledger.
 *  - PUT/DELETE /society/accounting/billing-mapping/{headCode}
 *
 * Per item 3/4 of the spec: mapping is per-Society, configurable here, and a
 * head with no mapping is a HARD STOP at posting time (not a silent
 * fallback) - this page is exactly where that gets fixed.
 */

function ledgerOptionsFor(ledgers, allowedGroups) {
  return ledgers
    .filter((l) => !allowedGroups || allowedGroups.has(l.ledgerGroup))
    .map((l) => ({
      value: l.id,
      label: l.name,
      sublabel: ledgerGroupLabels[l.ledgerGroup] || l.ledgerGroup,
      group: ledgerGroupLabels[l.ledgerGroup] || "Other",
      keywords: [l.code],
    }));
}

export default function BillingLedgerMappingPage() {
  const { ledgers, bankAccounts, cashLedgers, loading: optionsLoading } = useAccountOptions();

  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(true);
  const [editingHead, setEditingHead] = useState(null); // row object currently open in dialog
  const [editLedgerId, setEditLedgerId] = useState("");
  const [busyHead, setBusyHead] = useState(false);

  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsForm, setSettingsForm] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const loadRows = async () => {
    try {
      setRowsLoading(true);
      const res = await api.get("/society/accounting/billing-mapping");
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(formatErr(err, "Could not load Billing Head mappings"));
    } finally {
      setRowsLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      setSettingsLoading(true);
      const res = await api.get("/society/accounting/billing-settings");
      setSettings(res.data || {});
      setSettingsForm({
        memberReceivableLedgerId: res.data?.memberReceivableLedgerId || "",
        memberAdvanceLedgerId: res.data?.memberAdvanceLedgerId || "",
        bankAccountId: res.data?.bankAccountId || "",
        cashLedgerId: res.data?.cashLedgerId || "",
      });
    } catch (err) {
      toast.error(formatErr(err, "Could not load Billing Accounting settings"));
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => { loadRows(); loadSettings(); }, []);

  const incomeOptions = useMemo(() => ledgerOptionsFor(ledgers, new Set(["income"])), [ledgers]);
  const anyLedgerOptions = useMemo(() => ledgerOptionsFor(ledgers), [ledgers]);
  const cashOptions = useMemo(() => cashLedgers.map((l) => ({ value: l.id, label: l.name, group: "Cash" })), [cashLedgers]);
  const bankOptions = useMemo(() => bankAccounts.map((a) => ({
    value: a.id,
    label: [a.bankName, a.accountName, a.accountNumber].filter(Boolean).join(" — "),
    group: "Bank Accounts",
  })), [bankAccounts]);

  const openEdit = (row) => {
    setEditingHead(row);
    setEditLedgerId(row.ledgerId || "");
  };

  const saveMapping = async () => {
    if (!editingHead) return;
    if (!editLedgerId) {
      toast.error("Select an Income ledger to map this head to");
      return;
    }
    try {
      setBusyHead(true);
      await api.put(`/society/accounting/billing-mapping/${editingHead.headCode}`, { ledgerId: editLedgerId });
      toast.success(`${editingHead.headName} mapped`);
      setEditingHead(null);
      setEditLedgerId("");
      await loadRows();
    } catch (err) {
      toast.error(formatErr(err, "Could not save this mapping"));
    } finally {
      setBusyHead(false);
    }
  };

  const removeMapping = async (row) => {
    try {
      setBusyHead(true);
      await api.delete(`/society/accounting/billing-mapping/${row.headCode}`);
      toast.success(`${row.headName} mapping removed`);
      await loadRows();
    } catch (err) {
      toast.error(formatErr(err, "Could not remove this mapping"));
    } finally {
      setBusyHead(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSavingSettings(true);
      await api.put("/society/accounting/billing-settings", settingsForm);
      toast.success("Billing Accounting settings saved");
      await loadSettings();
    } catch (err) {
      toast.error(formatErr(err, "Could not save Billing Accounting settings"));
    } finally {
      setSavingSettings(false);
    }
  };

  const settingsDirty = settings && settingsForm && (
    settings.memberReceivableLedgerId !== settingsForm.memberReceivableLedgerId ||
    settings.memberAdvanceLedgerId !== settingsForm.memberAdvanceLedgerId ||
    settings.bankAccountId !== settingsForm.bankAccountId ||
    settings.cashLedgerId !== settingsForm.cashLedgerId
  );

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Billing Ledger Mapping</h2>
        <p className="mt-1 text-sm text-[#6B7280]">
          Controls how Society Billing (maintenance bills and payments) posts into the double-entry Accounting engine.
          A Billing Head with no mapping below is a hard stop, not a silent skip - Generate Bills will mark that bill's
          Accounting status as an error naming the unmapped head, rather than guessing or posting an incomplete entry.
        </p>
      </div>

      <SectionCard
        title="Member Receivables &amp; Settlement Accounts"
        subtitle="Required before any bill or payment can post to Accounting."
      >
        {settingsLoading || !settingsForm ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Member Receivables (control ledger)">
              <SearchableSelect
                value={settingsForm.memberReceivableLedgerId}
                onChange={(v) => setSettingsForm((f) => ({ ...f, memberReceivableLedgerId: v }))}
                options={anyLedgerOptions}
                loading={optionsLoading}
                placeholder="Select ledger"
                searchPlaceholder="Search ledgers..."
                emptyText="No ledgers found."
              />
              <p className="mt-1 text-xs text-[#9CA3AF]">Every member's bill and payment posts against this one control ledger, using the member/flat as the party dimension - not a separate GL account per flat.</p>
            </Field>
            <Field label="Member Advances (optional)">
              <SearchableSelect
                value={settingsForm.memberAdvanceLedgerId}
                onChange={(v) => setSettingsForm((f) => ({ ...f, memberAdvanceLedgerId: v }))}
                options={anyLedgerOptions}
                loading={optionsLoading}
                placeholder="Select ledger"
                searchPlaceholder="Search ledgers..."
                emptyText="No ledgers found."
                clearable
              />
              <p className="mt-1 text-xs text-[#9CA3AF]">If left blank, a payment that exceeds a bill's outstanding posts its full amount to Member Receivables as an honest temporary credit balance.</p>
            </Field>
            <Field label="Settlement Bank Account (non-cash payments)">
              <SearchableSelect
                value={settingsForm.bankAccountId}
                onChange={(v) => setSettingsForm((f) => ({ ...f, bankAccountId: v }))}
                options={bankOptions}
                loading={optionsLoading}
                placeholder="Select bank account"
                searchPlaceholder="Search bank accounts..."
                emptyText="No bank accounts found."
              />
              <p className="mt-1 text-xs text-[#9CA3AF]">Used for UPI / bank transfer / cheque / other payment modes. A real Accounting Bank Account - never a generic "Main Bank" ledger.</p>
            </Field>
            <Field label="Cash Ledger (cash payments)">
              <SearchableSelect
                value={settingsForm.cashLedgerId}
                onChange={(v) => setSettingsForm((f) => ({ ...f, cashLedgerId: v }))}
                options={cashOptions}
                loading={optionsLoading}
                placeholder="Select cash ledger"
                searchPlaceholder="Search..."
                emptyText="No cash-group ledgers found."
              />
            </Field>
            <div className="md:col-span-2">
              <Button disabled={!settingsDirty || savingSettings} onClick={saveSettings} className="bg-[#111827] text-white hover:bg-[#374151]">
                Save Settings
              </Button>
            </div>
          </div>
        )}
      </SectionCard>

      <SectionCard title="Billing Head → Income Ledger Mapping">
        {rowsLoading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Billing Head</TableHead>
                  <TableHead>Accounting Ledger</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.headCode}>
                    <TableCell className="font-semibold text-[#111827]">{row.headName}</TableCell>
                    <TableCell>{row.ledgerName || <span className="text-[#9CA3AF]">Not mapped</span>}</TableCell>
                    <TableCell><StatusPill active={row.status === "mapped"} trueLabel="Mapped" falseLabel="Unmapped" /></TableCell>
                    <TableCell className="text-xs text-[#6B7280]">{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : "-"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => openEdit(row)}><Pencil className="mr-1.5 h-3.5 w-3.5" /> {row.ledgerId ? "Change" : "Map"}</Button>
                        {row.ledgerId && (
                          <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" disabled={busyHead} onClick={() => removeMapping(row)}>Remove</Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center text-sm text-[#6B7280]">No active Billing Heads found.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </SectionCard>

      <Dialog open={!!editingHead} onOpenChange={(open) => { if (!open) { setEditingHead(null); setEditLedgerId(""); } }}>
        <DialogContent>
          {editingHead && (
            <>
              <DialogHeader>
                <DialogTitle>Map — {editingHead.headName}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <Field label="Income Ledger">
                  <SearchableSelect
                    value={editLedgerId}
                    onChange={setEditLedgerId}
                    options={incomeOptions}
                    loading={optionsLoading}
                    placeholder="Select an Income ledger"
                    searchPlaceholder="Search ledgers..."
                    emptyText="No Income-group ledgers found. Create one under Chart of Accounts / Ledgers first."
                  />
                </Field>
              </div>
              <DialogFooter>
                <Button variant="outline" disabled={busyHead} onClick={() => { setEditingHead(null); setEditLedgerId(""); }}>Cancel</Button>
                <Button disabled={busyHead} onClick={saveMapping} className="bg-[#111827] text-white hover:bg-[#374151]">Save</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
